import {
  AlertIcon,
  Banner,
  Flex,
  InfoIcon,
  Typography,
} from '@iheartjane/reefer';
import debounce from 'lodash-es/debounce';
import get from 'lodash-es/get';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { hex } from 'wcag-contrast';

import { ColorInput } from '@jane/business-admin/components/premium/ColorInput';
import type { ColorInputProps } from '@jane/business-admin/components/premium/ColorInput';

const WCAG_RATIO = 4.5;
const isColorAccessible = (foreground: string, background: string) =>
  hex(foreground, background) >= WCAG_RATIO;
const isHexColor = (color: string) => {
  const hexColorRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
  return hexColorRegex.test(color);
};

type COLOR_FIELD = {
  checkAccessibility?: boolean;
  label: string;
  name: string;
};

const DebouncedColorInput = ({
  onChange,
  value,
  ...props
}: ColorInputProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);

  if (value !== prevValue) {
    setPrevValue(value);
    setLocalValue(value);
  }

  const debouncedFormUpdate = useMemo(
    () =>
      debounce((nextValue: string) => {
        onChange(nextValue);
      }, 500),
    [onChange]
  );

  const handleOnChange = (val: string) => {
    setLocalValue(val);
    debouncedFormUpdate(val);
  };

  useEffect(() => {
    return () => debouncedFormUpdate.cancel();
  }, [debouncedFormUpdate]);

  return <ColorInput onChange={handleOnChange} value={localValue} {...props} />;
};

const ColorPickerInput = ({ label, name, checkAccessibility }: COLOR_FIELD) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const hasError = get(errors, name);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      border="grays-light"
      p={8}
      pl={16}
      borderRadius="sm"
    >
      <Flex>
        {hasError && <AlertIcon mr={4} />}
        <Typography>{label}</Typography>
      </Flex>
      <Controller
        name={name}
        control={control}
        rules={{
          validate: {
            hex: (value) => isHexColor(value) || label,
            a11y: (value) => {
              if (!checkAccessibility) {
                return;
              } else {
                return isColorAccessible(value, '#fff') || label;
              }
            },
          },
        }}
        render={({ field, fieldState }) => (
          <DebouncedColorInput
            value={field.value ?? '#'}
            onChange={field.onChange}
            onBlur={field.onBlur}
            hasError={!!fieldState.error}
          />
        )}
      />
    </Flex>
  );
};

const COLOR_FIELDS = [
  { label: 'Primary', name: 'colors.primary.main' },
  { label: 'Secondary', name: 'colors.secondary.main' },
  { label: 'Deals', name: 'colors.deals.main', checkAccessibility: true },
  { label: 'Header', name: 'colors.header.main' },
];

const LINEAGE_FIELDS = [
  {
    label: 'Hybrid',
    name: 'colors.lineage.hybrid.main',
    checkAccessibility: true,
  },
  {
    label: 'Indica',
    name: 'colors.lineage.indica.main',
    checkAccessibility: true,
  },
  {
    label: 'Sativa',
    name: 'colors.lineage.sativa.main',
    checkAccessibility: true,
  },
  { label: 'CBD', name: 'colors.lineage.cbd.main', checkAccessibility: true },
];

const buildColorsErrorMessage = (labels: string[], type: 'a11y' | 'hex') => {
  if (labels.length === 0) return '';

  const single =
    type === 'a11y'
      ? 'does not pass accessibility standards'
      : 'is not a valid hex color';
  const plural =
    type === 'a11y'
      ? "don't pass accessibility standards"
      : 'are not valid hex colors';

  if (labels.length === 1) {
    return `${labels[0]} color ${single}.`;
  }

  return `${labels.length} colors ${plural}.`;
};

export const ColorsSection = () => {
  const {
    formState: { errors },
    trigger,
    watch,
  } = useFormContext();
  const formValues = watch();

  useEffect(() => {
    // Trigger validation once when form values initially get set
    // This handles the case where invalid hex colors were previously saved
    trigger();
  }, [JSON.stringify(formValues)]);

  const hexColorErrorMessage = buildColorsErrorMessage(
    [...COLOR_FIELDS, ...LINEAGE_FIELDS]
      .filter(({ name }) => get(errors, name)?.type === 'hex')
      .map(({ label }) => label),
    'hex'
  );

  const a11yColorErrorMessage = buildColorsErrorMessage(
    [...COLOR_FIELDS, ...LINEAGE_FIELDS]
      .filter(({ name }) => get(errors, name)?.type === 'a11y')
      .map(({ label }) => label),
    'a11y'
  );

  return (
    <Flex gap={24} flexDirection="column">
      {hexColorErrorMessage && (
        <Banner
          icon={<AlertIcon />}
          label={hexColorErrorMessage}
          variant="error"
        />
      )}
      {a11yColorErrorMessage && (
        <Banner
          icon={<AlertIcon />}
          label={a11yColorErrorMessage}
          variant="error"
        />
      )}
      <div>
        <Typography variant="body-semibold">Theme</Typography>
        <Typography variant="small-body-regular" color="grays-mid" mb={12}>
          Used across categories and buttons
        </Typography>

        <Flex flexDirection="column" gap={16}>
          {COLOR_FIELDS.map((color) => (
            <ColorPickerInput key={color.name} {...color} />
          ))}
        </Flex>
      </div>

      <div>
        <Typography variant="body-semibold">Lineage</Typography>
        <Typography variant="small-body-regular" color="grays-mid" mb={12}>
          Strain type accents
        </Typography>

        <Flex flexDirection="column" gap={16}>
          {LINEAGE_FIELDS.map((lineage) => (
            <ColorPickerInput key={lineage.name} {...lineage} />
          ))}
        </Flex>

        <Flex mt={16} width="100%">
          <InfoIcon
            size="sm"
            mr={8}
            mt={4}
            color="grays-mid"
            style={{ flexShrink: 0 }}
          />
          <Typography variant="small-body-regular" color="grays-mid">
            Only Primary, Deals, and Header show in preview here. All other
            colors will be live on your menu.
          </Typography>
        </Flex>
      </div>
    </Flex>
  );
};
